import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { debounceTime, distinctUntilChanged, finalize, first, map, switchMap, tap } from 'rxjs/operators';
import { EventService } from 'src/app/service/event.service';
import { SolutionService } from 'src/app/service/solution.service';
import { Events } from 'src/app/service/websocket.service';
import { Utils } from 'src/app/utils';
import { emptyValidator } from 'src/app/validator/empty-form-validator';
import { ColumnType, DataTableColumn, DataTableDropDownData, DataTableDropDownOption, DataTableRow } from '../controls/datatable/datatable.component';
import { Tab, TabColor } from '../controls/tabs/tabs.component';

export enum ImportTab {
  ENVIRONMENTS,
  PENDING_SYSTEMS,
  PLANS,
  EXTERNAL_VENDOR_FILES,
  PRODUCTS
}

@Component({
    selector: 'app-import-solution-form',
    templateUrl: './import-solution-form.component.html',
    styleUrls: ['./import-solution-form.component.css'],
    standalone: false
})
export class ImportSolutionFormComponent implements OnInit {

  @Input() height: string = 'calc(100vh - 160px)';
  @Input() data: any;
  @Output() close = new EventEmitter<boolean>();

  ImportTab = ImportTab;
  objectForm: FormGroup;
  solutionid: string;
  solutionInfo: any;
  environmentTab: Tab = new Tab('Environments', $localize`Environments`, 0, true);
  systemsTab: Tab = new Tab('Pending Systems', $localize`Pending Systems`, 0, false);
  plansTab: Tab = new Tab('Plans', $localize`Plans`, 0, false);
  extFileTab: Tab = new Tab('External Vendor Files', $localize`External Vendor Files`, 0, false);
  productsTab: Tab = new Tab('Products', $localize`Products`, 0, false);
  importTabs: Tab[] = [this.environmentTab, this.systemsTab, this.plansTab, this.extFileTab, this.productsTab];
  activeTab: ImportTab = ImportTab.ENVIRONMENTS;
  importing: boolean = false;
  deleted: boolean = false;
  isError: boolean = false;
  importError: string = '';

  envCols: DataTableColumn[] = [];
  systemCols: DataTableColumn[] = [];
  planCols: DataTableColumn[] = [];
  fileTableCols: DataTableColumn[] = []

  envAgents: any[] = [];
  envRows: DataTableRow[] = [];
  systemRows: DataTableRow[] = [];
  planRows: DataTableRow[] = [];
  extRows: DataTableRow[] = [];
  productRows: DataTableRow[] = [];

  envOptions: DataTableDropDownOption[] = [new DataTableDropDownOption('Select an environment', '')];

  TabColor = TabColor;

  constructor(private solutionSrv: SolutionService,
    private eventSrv: EventService) {

    this.objectForm = new FormGroup({
      nameCtrl: new FormControl('', [
        emptyValidator()
      ], [this.checkIfSolNameIsAlreadyTaken()]),
      descriptionCtrl: new FormControl('', [
      ])
    });
  }

  get nameCtrl() { return this.objectForm.get('nameCtrl'); }
  get descriptionCtrl() { return this.objectForm.get('descriptionCtrl'); }

  ngOnInit(): void {

    this.solutionid = this.data.id;
    this.solutionInfo = this.data.solutionInfo;

    this.envCols = [new DataTableColumn('name', $localize`Name`, ColumnType.TEXTBOX, false, "50"),
    new DataTableColumn('description', $localize`Description`, ColumnType.TEXTBOX, false, "50")];

    this.systemCols = [new DataTableColumn('systemName', $localize`Name`, ColumnType.TEXTBOX, false, "50"),
    new DataTableColumn('environment', $localize`Environment`, ColumnType.DROPDOWN, false, "50")];

    this.planCols = [new DataTableColumn('name', $localize`Name`, ColumnType.STRING, false, "33.5"),
    new DataTableColumn('description', $localize`Description`, ColumnType.STRING, false, "33.5"),
    new DataTableColumn('environment', $localize`Environment`, ColumnType.DROPDOWN, false, "33")];

    this.fileTableCols = [new DataTableColumn('name', $localize`Name`, ColumnType.STRING, false, "50"),
    new DataTableColumn('size', $localize`Size`, ColumnType.FILESIZE, false, "50")];

    for (let i = 0; i < this.solutionInfo.solution.environments.length; i++){
      let env = this.solutionInfo.solution.environments[i];
      this.envRows.push(new DataTableRow(env, []));
      this.envOptions.push(new DataTableDropDownOption(env.name, env.name));
      if (env.agents.length > 0) {
        env.agents.forEach(agent => {
          let tmpAgent = agent;
          tmpAgent.solution = env.solName;
          tmpAgent.environment = env.name;
          this.envAgents.push(tmpAgent);
        });
      }
      
      let formNameCtrlName: string = "envNameCtrl-" + i;
      let tmpFormNameControl = new FormControl(undefined, [emptyValidator(), this.checkEnvName(formNameCtrlName)]);
      tmpFormNameControl.setValue(env.name);
      this.objectForm.addControl(formNameCtrlName, tmpFormNameControl);
      env.nameCtrlName = formNameCtrlName;
      env.nameCtrl = tmpFormNameControl;
      
      let formDescCtrlName: string = "envDescCtrl-" + i;
      let tmpFormDescControl = new FormControl();
      tmpFormDescControl.setValue(env.description);
      this.objectForm.addControl(formDescCtrlName, tmpFormDescControl);
      env.descCtrlName = formDescCtrlName;
      env.descCtrl = tmpFormDescControl;
    }

    this.environmentTab.count = this.envRows.length;

    this.solutionInfo.deploymentPlans.forEach(plan => {
      plan.environment = new DataTableDropDownData(plan.selectedEnvironmentName, this.envOptions);
      this.planRows.push(new DataTableRow(plan, []));
    });
    this.plansTab.count = this.planRows.length;

    this.solutionInfo.extVenFiles.forEach(extFile => {
      extFile.size = extFile.byteSize;
      this.extRows.push(new DataTableRow(extFile, []));
    });
    this.extFileTab.count = this.extRows.length;

    this.solutionInfo.productFiles.forEach(product => {
      product.size = product.totalSize;
      this.productRows.push(new DataTableRow(product, []));
    });
    this.productsTab.count = this.productRows.length;

    this.data.unregisteredAgents.forEach(agent => {
      let agentEnv: string = '';
      for(let i = 0; i < this.envAgents.length; i++){
        if(this.envAgents[i].systemName === agent.systemName){
          agentEnv = this.envAgents[i].environment;
          break;
        }
      }
      agent.environment = new DataTableDropDownData(agentEnv, this.envOptions);
      this.systemRows.push(new DataTableRow(agent, []));
    });
    this.systemsTab.count = this.systemRows.length;

    setTimeout(() => {
      this.nameCtrl.setValue(this.solutionInfo.solution.name);
      this.descriptionCtrl.setValue(this.solutionInfo.solution.description);
      this.objectForm.markAllAsTouched();
    }, 0);

  }

  ngOnDestroy(): void {
    if (!this.deleted) {
      this.solutionSrv.deleteImportCache(this.data.id).pipe(
        tap(resp => {
          this.deleted = true;
        })).subscribe();
    }
  }

  closeForm(imported: boolean): void {
    if (!imported) {
      this.solutionSrv.deleteImportCache(this.data.id).pipe(
        tap(resp => {
          this.deleted = true;
          this.close.emit(imported);
        })).subscribe();
    } else {
      this.close.emit(imported);
    }
  }

  import(): void {
    this.isError = false;
    this.importing = true;
    this.objectForm.disable();

    let newSolution = Object.assign({}, this.solutionInfo);
    newSolution.solution.name = this.nameCtrl.value.trim();
    newSolution.solution.description = this.descriptionCtrl.value.trim();

    newSolution.solution.environments.forEach(env => {
      env.name = env.nameCtrl.value.trim();
      env.description = env.descCtrl.value.trim();
      env.nameCtrl = undefined;
      env.nameCtrlName = undefined;
      env.descCtrl = undefined;
      env.descCtrlName = undefined;
      env.agents = [];
    });

    this.systemRows.forEach(systemRow => {
      if (systemRow.data.environment.value !== '') {
        for (let i = 0; i < newSolution.solution.environments.length; i++) {
          if (newSolution.solution.environments[i].name === systemRow.data.environment.value) {
            let agent = systemRow.data;
            delete agent.environment;
            newSolution.solution.environments[i].agents.push(agent);
            break;
          }
        }
      }
    });

    this.planRows.forEach(planRow => {
      if (planRow.data.environment.value !== '') {
        for (let i = 0; i < newSolution.deploymentPlans.length; i++) {
          if (newSolution.deploymentPlans[i].name === planRow.data.name) {
            newSolution.deploymentPlans[i].selectedEnvironmentName == planRow.data.environment.value;
            break;
          }
        }
      }
    });

    if (!this.isError){
      this.solutionSrv.importSolution(this.solutionid, newSolution).pipe(
        tap(resp => {
          if (resp.body.status.success) {
            Utils.addHeaderMessageEventBanner(this.eventSrv, Events.SuccessMsg, $localize`Solution '${resp.body.result.data.solutionName}' was imported successfully.`);

          } else {
            switch (resp.body.status.errorCode) {
              case 'ERROR_IMPORT_ENVIRONMENT_INFO':
                this.importError = $localize`Unable to import environment '${resp.body.status.error}'. Please try again.`;
                break;
              case 'ERROR_IMPORT_EXT_VENDOR_FILE':
                this.importError = $localize`Unable to import external vendor files. Please try again.`;
                break;
              case 'ERROR_IMPORT_PROD_FILE':
                this.importError = $localize`Unable to import product files. Please try again.`;
                break;
              case 'ERROR_IMPORT_REGISTER_AGENT':
                this.importError = $localize`Unable to register system '${resp.body.status.error}'`;
                break;
              case 'ERROR_IMPORT_SOLUTION_PLAN':
                this.importError = resp.body.status.error;
                break;
              case 'ERROR_IMPORT_SOLUTION':
                this.importError = $localize`Error importing solution. Please try again.`;
                break;
              case 'ERROR_IMPORT_SOLUTION_FILE_NOT_FOUND':
                this.importError = $localize`Unable to import solution, the required file 'solution.json' was not found in the package. Please try again."`;
                break;
              case 'ERROR_IMPORT_SOLUTION_STAGE_FOLDER_NOT_FOUND':
                this.importError = $localize`Unable to find staging folder. Please try again.`;
                break;
              case 'ERROR_IMPORT_SOLUTION_INFO':
                this.importError = $localize`Unable to import solution '${resp.body.status.error}'. Please try again.`;
                break;
            }
            Utils.addHeaderMessageEventBanner(this.eventSrv, Events.ErrorMsg, this.importError);
          }
        }),
        finalize(() => {
          this.closeForm(true);
        })
      ).subscribe();
    }else{
      this.importing = false;
      this.objectForm.enable();
    }
  }

  onSelectedTabChange(selectedIndex: number) {
    this.activeTab = selectedIndex;
  }

  checkIfSolNameIsAlreadyTaken(): AsyncValidatorFn {
    return control => control.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(value => this.solutionSrv.validateSolutionName(value.trim())),
      map((result) => (result.body ? null : { exists: true })),
      first()
    )
  }

  checkEnvName(name: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      setTimeout(() => {
        this.validateDuplicateEnvNames(name);
      }, 0);
      return (this.isDuplicateEnv(control.value)) ? { 'exists': true } : null;
    };
  }

  validateDuplicateEnvNames(ctrlName: string): void {
    Object.keys(this.objectForm.controls).forEach(ctrlKey => {
      if (ctrlKey.indexOf("envNameCtrl") > -1) {
        if (ctrlKey !== ctrlName) {
          if (this.isDuplicateEnv(this.objectForm.controls[ctrlKey].value)) {
            this.objectForm.controls[ctrlKey].setErrors({ 'exists': true });
          } else {
            this.objectForm.controls[ctrlKey].setErrors(undefined);
          }
        }
      }
    });
  }

  isDuplicateEnv(name: string): boolean {
    return this.solutionInfo.solution.environments.filter((env: any) => {
      return name !== null && env.nameCtrlName !== undefined && this.objectForm.controls[env.nameCtrlName].value.trim() == name.trim();
    }).length > 1;
  }

  changeName(env: any){
    for (let i = 0; i < this.solutionInfo.solution.environments.length; i++){
      if (env.name == this.solutionInfo.solution.environments[i].name){
        this.updateEnvOptions(env);
        this.updateSystemRowOptions(env.name, env.nameCtrl.value);
        this.updatePlanRowOptions(env.name, env.nameCtrl.value);
        this.solutionInfo.solution.environments[i].name = env.nameCtrl.value;
        break;
      }
    }
  }

  updateEnvOptions(env: any){
    for (let i = 0; i < this.envOptions.length; i++) {
      if (env.name == this.envOptions[i].name) {
        this.envOptions[i].name = env.nameCtrl.value.trim();
        this.envOptions[i].value = env.nameCtrl.value.trim();
        break;
      }
    }
  }

  updateSystemRowOptions(oldValue: string, newValue: string): void{
    for (let i = 0; i < this.systemRows.length; i++){
      if (this.systemRows[i].data.environment.value !== undefined && this.systemRows[i].data.environment.value !== "" &&
        this.systemRows[i].data.environment.value == oldValue) {
          this.systemRows[i].data.environment.value = newValue;
          break;
      }
    }
  }

  updatePlanRowOptions(oldValue: string, newValue: string): void {
    for (let i = 0; i < this.planRows.length; i++) {
      if (this.planRows[i].data.environment.value !== undefined && this.planRows[i].data.environment.value !== "" && 
        this.planRows[i].data.environment.value == oldValue) {
        this.planRows[i].data.environment.value = newValue;
        this.updatePlansSolutionInfo(this.planRows[i].data.name, newValue);
        break;
      }
    }
  }

  updatePlansSolutionInfo(plan: string, env: string): void{
    for(let i = 0; i < this.solutionInfo.deploymentPlans.length; i++){
      if (this.solutionInfo.deploymentPlans[i].name == plan){
        this.solutionInfo.deploymentPlans[i].selectedEnvironmentName = env;
        break;
      }
    }
  }
}
